import { styled } from '@linaria/react'
import React, { type FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { colors } from '../styles/colors'
import { ButtonRegular } from '../ui/Button'
import Close from '../svgr/Close'

type DialogState = 'closed' | 'opening' | 'open' | 'closing'

interface DialogChildrenProps {
  onClose: VoidFunction
}
type DialogChildren =
  | React.ReactNode
  | ((props: DialogChildrenProps) => React.ReactNode)

export const Dialog: FC<{
  onClose: VoidFunction
  isNeedWallet?: boolean
  children: DialogChildren
}> = ({ children, onClose }) => {
  const [state, setState] = useState<DialogState>('closed')

  useEffect(() => {
    setState('opening')

    // Save current overflow value and disable body scroll
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Trigger opening animation on next frame
    requestAnimationFrame(() => {
      setState('open')
    })

    return () => {
      // Restore original overflow value
      document.body.style.overflow = originalOverflow
    }
  }, [])

  const handleClose = () => {
    setState('closing')
  }

  const handleTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    // Only handle transition end for the backdrop itself, not child elements
    if (event.target === event.currentTarget && state === 'closing') {
      setState('closed')
      onClose()
    }
  }

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleClose()
    }
  }

  const modalRoot = document.getElementById('modal')

  // Support both render prop and regular children
  const renderedChildren =
    typeof children === 'function'
      ? children({ onClose: handleClose })
      : children

  return createPortal(
    <DialogBackdrop
      data-testid="dialog-backdrop"
      isVisible={state === 'open'}
      onClick={handleBackdropClick}
      onTransitionEnd={handleTransitionEnd}
    >
      <DialogContent isVisible={state === 'open'}>
        <DialogInner>
          <CloseButton
            data-testid="dialog-close"
            onClick={handleClose}
            noShadow={true}
          >
            <Close />
          </CloseButton>
          {renderedChildren}
        </DialogInner>
      </DialogContent>
    </DialogBackdrop>,
    modalRoot || document.body,
  )
}

const DialogBackdrop = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: ${colors.shadowDark};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`
const DialogInner = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  gap: 48px;
  background: ${colors.bgCard};
  padding: 32px;
  width: 100%;
  max-width: 400px;
  text-align: center;
  border-radius: ${colors.radiusCardLg};
  box-shadow: ${colors.shadowLg};
`
const DialogContent = styled.div<{ isVisible: boolean }>`
  border-radius: 20px;
  width: 90%;
  max-width: 370px;
  transform: ${props => (props.isVisible ? 'scale(1)' : 'scale(0.95)')};
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
`

const CloseButton = styled(ButtonRegular)`
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: none;

  svg {
    width: 16px;
    height: 16px;

    path {
      fill: ${colors.textSecondary};
    }
  }
`
